// src/features/execucao/components/ExecucaoCard.jsx
import React from "react";
import styles from "./ExecucaoCard.module.css";
import SdExecucaoTimer from "./SdExecucaoTimer";
import { toKeyYYYYMMDD, toHHMM, todayKeyBR } from "../../../utils/datesBR";

export const STATUS_LABEL = {
  em_andamento: "Não Iniciado",
  primeira_previa: "Primeira Prévia",
  segunda_previa: "Segunda Prévia",
  executado: "Executado",
  reprogramar: "Reprogramar",
  finalizado: "Finalizado",
};

export function getStatusKey(p) {
  const wf = (p?.status_workflow || "").toLowerCase();
  const fin = (p?.status_finalizacao || "").toLowerCase();
  if (/finaliz/.test(fin) || /finaliz/.test(wf)) return "finalizado";
  if (/reprog/.test(fin)  || /reprog/.test(wf))  return "reprogramar";
  if (/execut/.test(fin)  || /execut/.test(wf))  return "executado";
  if (/segunda/.test(fin) || fin === "2" || fin === "segunda_previa")   return "segunda_previa";
  if (/primeira/.test(fin) || fin === "1" || fin === "primeira_previa")  return "primeira_previa";
  if (/andamento/.test(fin) || /andamento/.test(wf)) return "em_andamento";
  if ((p?.segunda_previa || "").trim())  return "segunda_previa";
  if ((p?.primeira_previa || "").trim()) return "primeira_previa";
  return "em_andamento";
}

export const STATUS_ORDER = {
  // ✅ ordem pedida:
  em_andamento: 0,        // “Não Iniciado”
  primeira_previa: 1,
  segunda_previa: 2,

  // não citados pelo usuário → vão antes dos finalizados
  executado: 3,
  reprogramar: 3,

  finalizado: 4,          // “por fim os finalizados”
};

export function statusOrderIndex(p) {
  return STATUS_ORDER[getStatusKey(p)] ?? 99;
}

/* ===== helpers visuais ===== */
function hasRealStartValue(v) {
  const s = (v == null ? "" : String(v)).trim();
  if (!s || s === "—" || s === "-") return false;
  if (/^0{1,2}:0{2}(?::0{2})?$/.test(s)) return false;
  return true;
}
function ClockIcon({ size = 12, stroke = 2 }) {
  return (
    <svg className={styles.clockIcon} width={size} height={size} viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round"
      strokeLinejoin="round" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 3" />
    </svg>
  );
}

export default function ExecucaoCard({ programacao, nowTs, onSelect }) {
  const hojeKey = todayKeyBR();
  const p = programacao || {};
  const id = p.codigo_unico || p.id || "";
  const dayBase = toKeyYYYYMMDD(p.__dataKey ?? p.data_key ?? p.dataKey ?? p.data) || hojeKey;

  const sKey = getStatusKey(p);
  const statusClass = styles[`st_${sKey}`] || "";
  const aguardandoInicio = ![p.primeiro_inicio, p.segundo_inicio, p.terceiro_inicio].some(hasRealStartValue);

  // ⬇️ força a ordem se o container for flex
  const orderIdx = statusOrderIndex(p);

  return (
    <div
      className={`${styles.card} ${statusClass}`}
      style={{ order: orderIdx }}
      role="button"
      onClick={() => onSelect?.({ ...p, __dataKey: dayBase })}
      title="Abrir ficha de execução"
      data-status={sKey}           // útil p/ debug/QA no DOM
      data-order={orderIdx}
    >
      <div className={styles.metaCol} aria-hidden>
        <div className={styles.pillBox}>
          <span className={`${styles.pill} ${styles.pillCode}`}>{id || "—"}</span>
        </div>
        <div className={styles.pillBox}>
          <span className={`${styles.pill} ${styles.pillStatus}`}>{STATUS_LABEL[sKey]}</span>
        </div>
        <div className={styles.pillBox}>
          <span className={`${styles.pill} ${styles.pillTimer}`}
            title={aguardandoInicio ? "Aguardando início" : "Cronômetro"}>
            {aguardandoInicio ? (
              <ClockIcon size={12} />
            ) : (
              <span className={styles.timerInline}>
                <SdExecucaoTimer
                  nowTs={nowTs}
                  day={dayBase}
                  tempo_total={p.tempo_total || p.tempo_do_intervalo || "00:10"}
                  tempo_do_intervalo={p.tempo_do_intervalo}
                  primeiro_inicio={p.primeiro_inicio}
                  primeiro_fim={p.primeiro_fim}
                  segundo_inicio={p.segundo_inicio}
                  segundo_fim={p.segundo_fim}
                  terceiro_inicio={p.terceiro_inicio}
                  terceiro_fim={p.terceiro_fim}
                />
              </span>
            )}
          </span>
        </div>
      </div>

      <div className={styles.cardBody}>
        <div><b>Gerência:</b> {p.gerencia || "—"}</div>
        <div><b>Coordenador:</b> {p.coordenador || "—"}</div>
        <div><b>Atividade:</b> {p.atividade || "—"}</div>
        <div><b>Ativo:</b> {p.ativo || "—"}</div>
        <div>
          <b>Prioridade:</b> {p.prioridade || p.po || "—"}
          {p.qtd ? <> | <b>Quantidade:</b> {p.qtd}</> : null}
        </div>
        <div>
          <b>Origem:</b> {p.sbOrigem || "—"}
          <b style={{ marginLeft: 8 }}>Trab.:</b> {p.sbDeTrabalho || "—"}
          <b style={{ marginLeft: 8 }}>Encerr.:</b> {p.sbEncerramento || "—"}
        </div>
        <div><b>KM:</b> {p.kmInicio || "—"}{p.kmFinal ? ` - ${p.kmFinal}` : ""}</div>
        <div>
          <b>Início:</b> {toHHMM(p.horario_do_intervalo)}{" "}
          <b style={{ marginLeft: 8 }}>Intervalo:</b> {toHHMM(p.tempo_do_intervalo)}
        </div>
        <div><b>Responsável pelo Aceite:</b> {p.responsavel_pelo_aceite || "—"}</div>
      </div>
    </div>
  );
}
